/// <reference types="../reports/types" />
/// <reference types="./types" />

import ko from 'knockout';
import _, { isNumber } from 'lodash';
import { ConditionGroupOperator } from './ConditionGroupOperator';
import { ConditionOperator } from './ConditionOperator';
import { filterColumnSupportsListValues } from './filterColumnSupportsListValues';
import { updateFilterOperators } from './updateFilterOperators';

/**
 * Converts the specified API filters to filters for the report editor UI.
 * 
 * @param {ConditionGroup} filters The API filters to convert.
 * @param {ConditionGroupOperator} parentOperator The operator of the group's parent group.
 * @param {Column[]} columns The columns referenced by the filters.
 * @param {Column[]} listValueColumns The columns that support list-of-value selection.
 * 
 * @returns {unknown[]} The filters for the report editor UI.
 */
export function apiFiltersToUIFilters({ conditions, groups, operator }, parentOperator, columns, listValueColumns) {
    conditions = [...conditions, ...groups].sort((a, b) => {
        if (a.order < b.order) {
            return -1;
        }
        if (a.order > b.order) {
            return 1;
        }
        return 0;
    });

    if (isListOfValues(conditions, operator)) {
        return [conditionsToListOfValues(conditions, parentOperator, columns, listValueColumns)];
    }

    const filters = [];

    for (const [i, conditionOrGroup] of conditions.entries()) {
        if (conditionOrGroup.column) {
            filters.push(conditionToFilter(conditionOrGroup, i > 0 ? operator : parentOperator, columns, listValueColumns));
        } else {
            filters.push(...apiFiltersToUIFilters(conditionOrGroup, operator, columns, listValueColumns));
        }
    }

    return filters;
}

/**
 * Converts the specified API condition to a filter for the report editor UI.
 * 
 * @param {Condition} condition The condition to convert.
 * @param {ConditionGroupOperator} groupOperator The boolean operator to apply to the filter.
 * @param {Column[]} columns The columns referenced by the filters.
 * @param {Column[]} listValueColumns The columns that support list-of-value selection.
 * 
 * @returns {unknown} The converted filter.
 */
function conditionToFilter(condition, groupOperator, columns, listValueColumns) {
    const filter = {};

    const list = ['list', 'nlist'].includes(condition.operator);

    const operator = ko.observable(list ? condition.operator : getUIOperator(condition));

    let type;

    switch (groupOperator) {
        case ConditionGroupOperator.And:
            type = 'and';
            break;

        case ConditionGroupOperator.Or:
            type = 'or';
            break;

        default:
            throw new Error(
                `Could not convert the filter group to a UI filter because it has an invalid operator (${groupOperator}).`
            );
    }

    filter.columnId = ko.observable(condition.column);
    filter.column = _.find(columns, function (c) { return c.columnId === condition.column; });
    filter.type = ko.observable(type);
    filter.operator = operator;
    filter.showFilterSelector = ko.observable(false); //By default, do not show the filter selector on each filter
    filter.filterOperators = ko.observableArray([]);
    filter.value = ko.observable(list ? "" : condition.value);
    filter.sourceType = ko.observable(condition.sourceType);

    var newListValues = ko.observableArray([]);
    _.forEach(condition.listValues, function (v) {
        if (typeof v != "undefined" && ((typeof v.length != "undefined" && v.length > 0) || (isNumber(v))))
            newListValues.push(v);       
    });
    filter.listValues = newListValues;

    filter.filterListValuesEnabled = ko.observable(filterColumnSupportsListValues(filter, listValueColumns) && (filter.operator() === "list" || filter.operator() === "nlist")); //Check to see if this filter should have list values enabled.
    updateFilterOperators(filter, listValueColumns);

    return filter;
}

/**
 * Converts the specified API conditions to a single list-of-values UI filter.
 * 
 * Assumes that the conditions form a valid list-of-values group (i.e. isListOfValues returns true).
 * 
 * @param {Condition[]} conditions The conditions to convert.
 * @param {ConditionGroupOperator} groupOperator The boolean operator to apply to the filter.
 * @param {Column[]} columns The columns referenced by the filters.
 * @param {Column[]} listValueColumns The columns that support list-of-value selection.
 * 
 * @returns {unknown} The converted list-of-values filter.
 */
function conditionsToListOfValues(conditions, groupOperator, columns, listValueColumns) {
    const condition = {
        ...conditions[0],
        listValues: conditions.map(({ value }) => value),
        operator: conditions[0].operator === ConditionOperator.Equal ? 'list' : 'nlist'
    };

    return conditionToFilter(condition, groupOperator, columns, listValueColumns);
}

/**
 * Retrieves the filter editor UI operator for the specified API condition.
 * 
 * @param {Condition} condition The condition whose UI operator will be retrieved.
 * 
 * @throws The condition's operator is invalid.
 */
function getUIOperator({ operator }) {
    switch (operator) {
        case ConditionOperator.Contains:
            return 'contains';
        case ConditionOperator.EndsWith:
            return 'endswith';
        case ConditionOperator.Equal:
            return 'eq';
        case ConditionOperator.GreaterThan:
            return 'gt';
        case ConditionOperator.GreaterThanOrEqual:
            return 'ge';
        case ConditionOperator.LessThan:
            return 'lt';
        case ConditionOperator.LessThanOrEqual:
            return 'le';
        case ConditionOperator.NotContains:
            return 'doesnotcontain';
        case ConditionOperator.NotEqual:
            return 'ne';
        case ConditionOperator.StartsWith:
            return 'startswith';
    }

    throw new Error(
        `Could not get the UI operator for the condition because it has an invalid operator (${operator}).`
    );
}


/**
 * Indicates whether the specified collection of conditions can be interpreted as a list-of-values filter in a group
 * with the specified operator.
 * 
 * Returns true if all of the following are true:
 * * The collection contains only conditions (not groups).
 * * The collection contains more than one condition.
 * * Every condition acts on the same column.
 * * Either the specified operator is And and every condition's operator is NotEqual,
 * or the specified operator is Or and every condition's operator is Equal.
 * 
 * Otherwise returns false.
 * 
 * @param {Array<Condition | ConditionGroup>} conditionOrGroups The conditions to test.
 * @param {ConditionGroupOperator} operator The operator of the group to which the conditions belong.
 */
function isListOfValues(conditionOrGroups, operator) {
    const isCondition = conditionOrGroups.every(conditionOrGroup => conditionOrGroup.column);

    if (!isCondition || conditionOrGroups.length < 2) {
        return false;
    }

    const conditions = conditionOrGroups;

    const [{ column }] = conditions;

    if (conditions.some(condition => condition.column !== column)) {
        return false;
    }

    return operator === ConditionGroupOperator.And
        ? conditions.every(condition => condition.operator === ConditionOperator.NotEqual)
        : conditions.every(condition => condition.operator === ConditionOperator.Equal);
}
